import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { arrayRemove, EMPTY_LIST } from '@/utils';
import classnames from 'classnames';
import { noop } from 'lodash-es';
import { useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import { LoadingIcon } from '../Icon';
import { NFTItem, NFTItemSkeleton } from '../NFTItem';
import { useLocales } from '../useLocales';
import styles from './index.module.less';
export const SelectableNFTList = ({ contractName, tokens, pendingSize = 0, loading, limit, selectedTokenIds = EMPTY_LIST, onChange, className, ...rest }) => {
    const t = useLocales();
    const allIds = useMemo(() => tokens.map((token) => token.tokenId), [tokens]);
    const isSelectedAll = allIds.length === selectedTokenIds.length;
    const [lastIndex, setLastIndex] = useState(null);
    const reachedLimit = selectedTokenIds.length >= limit;
    const toggleItem = useCallback((currentId) => {
        if (!onChange)
            return;
        let newIds = [...selectedTokenIds];
        if (selectedTokenIds.includes(currentId)) {
            arrayRemove(newIds, currentId);
        }
        else if (!reachedLimit) {
            newIds = [...selectedTokenIds, currentId];
        }
        onChange(newIds);
    }, [selectedTokenIds, reachedLimit]);
    const toggleSelectAll = (evt) => {
        if (!onChange)
            return;
        if (evt.currentTarget.checked) {
            if (isSelectedAll) {
                onChange(EMPTY_LIST);
            }
            else {
                const outOfBoundarySelectedIds = selectedTokenIds.filter((id) => {
                    const index = allIds.indexOf(id);
                    return index > limit - 1;
                });
                const withinBoundaryIds = allIds.slice(0, limit - outOfBoundarySelectedIds.length);
                onChange([...withinBoundaryIds, ...outOfBoundarySelectedIds]);
            }
        }
        else {
            onChange(EMPTY_LIST);
        }
    };
    const toggleRange = (from, to) => {
        if (!onChange)
            return;
        const [min, max] = from > to ? [to, from] : [from, to];
        const rangeIds = tokens.slice(min, max + 1).map((t) => t.tokenId);
        const previouslyChecked = selectedTokenIds.includes(tokens[to].tokenId);
        const remainingAmount = limit - selectedTokenIds.length;
        const changingIds = previouslyChecked
            ? rangeIds
            : rangeIds.slice(0, Math.min(remainingAmount, limit) + 1);
        const newIds = allIds.filter((id) => {
            if (changingIds.includes(id))
                return !previouslyChecked;
            return selectedTokenIds.includes(id);
        });
        onChange(newIds);
    };
    const handleItemClick = (evt, token, index) => {
        const disabled = !selectedTokenIds.includes(token.tokenId) && reachedLimit;
        if (disabled)
            return;
        setLastIndex(index);
        if (evt.shiftKey && lastIndex !== null) {
            toggleRange(lastIndex, index);
        }
        else {
            toggleItem(token.tokenId);
        }
    };
    const selectAllRef = useRef(null);
    const selectedSome = !isSelectedAll && selectedTokenIds.length > 0;
    useEffect(() => {
        if (!selectAllRef.current)
            return;
        selectAllRef.current.indeterminate = selectedSome;
    }, [selectedSome]);
    return (_jsxs("div", { className: classnames(className, styles.selectList), ...rest, children: [_jsxs("div", { className: styles.operations, children: [_jsxs("label", { className: classnames(styles.toggleAll, loading ? styles.locked : null), children: [_jsx("input", { ref: selectAllRef, type: "checkbox", disabled: loading, checked: isSelectedAll, onChange: toggleSelectAll }), t('Select all'), loading && _jsx(LoadingIcon, { size: 14 })] }), _jsxs("div", { className: styles.tip, children: ["You can also use ", _jsx("kbd", { children: "Shift" }), " to select multiple NFTs."] })] }), _jsxs("ul", { className: classnames(styles.list, styles.scrolling), children: [tokens.map((token, index) => (_jsxs("li", { className: styles.item, children: [_jsx(NFTItem, { contractName: contractName, token: token, className: styles.nft, disabled: !selectedTokenIds.includes(token.tokenId) && reachedLimit, hoverEffect: false, onClick: (evt) => {
                                    handleItemClick(evt, token, index);
                                } }), _jsx("input", { type: "checkbox", className: styles.checkbox, checked: selectedTokenIds.includes(token.tokenId), onChange: noop, onClick: (evt) => {
                                    handleItemClick(evt, token, index);
                                } })] }, token.tokenId))), loading &&
                        Array.from({ length: pendingSize }).map((_, index) => (_jsx("li", { className: styles.item, children: _jsx(NFTItemSkeleton, { className: styles.nft }) }, `skeleton${index}`)))] })] }));
};
